/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Action, NgxsOnInit, Selector, State, Store } from '@ngxs/store';
import { StateContext } from '@ngxs/store/src/symbols';
import {
    LayerStateChange,
    LoadMapConfig,
    MapConfigLoaded,
    PersistUserMapConfigState,
    ReloadLayers,
    ResetMapConfigState,
    ResetUserMapConfigState,
    SetGroupVisibility,
    SetLayerVisibility,
    SetNodeExpandedState,
} from './map-config.actions';
import { RestService } from '../../shared/services/rest.service';
import { concatMap, filter, mergeMapTo, tap } from 'rxjs/operators';
import { LoggerService } from '../../shared/services/logger.service';
import { InitializeMapState } from './active-map.actions';
import { MapConfigModel, MsGroupNode, UserMapConfigModel, UserMsGroupNode } from './map-config.model';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { ActiveMapState } from './active-map.state';
import { Injectable } from '@angular/core';
import { patch } from '@ngxs/store/operators';
import { of } from 'rxjs';
import { AppConfigState } from '../../../core/state/appConfig/app-config.state';
import { AuthState } from '../../../core/security/state/authState.state';
import { MapConfigService } from '../services/mapConfig.service';

export const MAPCONFIG_DEFAULT_STATE: MapConfigModel = {
    loaded: false,
    name: '',
    title: '',
    crs: 'EPSG:3857',
    projection: 'EPSG:3857',
    extent: [-1000.0, -1000.0, 1000.0, 1000.0],
    center: [0.0, 0.0],
    initialExtent: [-1000.0, -1000.0, 1000.0, 1000.0],
    units: 'm',
    refreshInterval: null,
    root: {
        name: '',
        label: '',
        expand: true,
        expandable: true,
        disableToggle: false,
        status: true,
        legendOrder: 0,
        layers: [],
        groups: [],
    },
    visibleRoot: {
        name: '',
        label: '',
        expand: true,
        expandable: true,
        disableToggle: false,
        status: true,
        legendOrder: 0,
        layers: [],
        groups: [],
    },
    sortedLayers: [],
    styles: {},
    toolbarWidgets: [],
    bottomSheetWidgets: [],
    contextMenuItems: [],
    userCustom: false,
};

@State<MapConfigModel>({
    name: 'mapConfig',
    defaults: MAPCONFIG_DEFAULT_STATE,
})
@Injectable()
export class MapConfigState implements NgxsOnInit {
    ngxsOnInit(ctx: StateContext<any>) {
        this.store
            .select(AppConfigState.loaded)
            .pipe(
                filter((configLoaded) => configLoaded),
                mergeMapTo(this.store.select(AuthState.validated)),
                filter((authenticated) => authenticated),
                mergeMapTo(this.store.select(AppConfigState.currentMap)),
                filter((currentMap) => !!currentMap)
            )
            .subscribe((currentMap: string) => {
                ctx.dispatch(new LoadMapConfig(currentMap));
            });
    }

    constructor(private logger: LoggerService, private rest: RestService, private store: Store) {}

    @Action(LoadMapConfig)
    loadMapConfig(ctx: StateContext<MapConfigModel>, action: LoadMapConfig) {
        return this.rest
            .apiGet(`map/layers/${action.mapName}`)
            .pipe(
                tap((config: MapConfigModel) => {
                    config.loaded = true;
                    const newState: MapConfigModel = DashGisUtil.clone(ctx.getState());
                    ctx.setState({ ...newState, ...config });
                })
            )
            .subscribe(() => {
                this.store.dispatch(new MapConfigLoaded());
            });
    }

    @Action(MapConfigLoaded)
    mapConfigLoaded({ getState, setState, dispatch }: StateContext<MapConfigModel>) {
        const mapConfig = this.store.selectSnapshot(MapConfigState.config);

        return dispatch(
            new InitializeMapState({
                center: mapConfig.center,
                extent: mapConfig.initialExtent || mapConfig.extent,
                zoom: 15,
            })
        ).pipe(
            tap(() => {
                // this is the same code as in LayerStateChange action handler,
                // but since map.service listens for that to update layers and
                // depends on the map being initialized, we do not call it here
                const state = DashGisUtil.clone(getState());
                const scale = this.store.selectSnapshot(ActiveMapState.scale);
                state.visibleRoot = this.removeOutOfRange(DashGisUtil.clone(state.root), scale);
                setState(state);
            })
        );
    }

    @Action(ReloadLayers)
    reloadLayers({ getState, setState, dispatch }: StateContext<MapConfigModel>) {
        const state = getState();
        return this.rest.apiGet(`map/layers/${state.name}`).pipe(
            tap((config: MapConfigModel) => {
                config.loaded = true;
                setState(
                    patch({
                        userCustom: config.userCustom,
                        root: config.root,
                    })
                );
            }),
            tap(() => dispatch(new LayerStateChange()))
        );
    }

    @Action(LayerStateChange)
    layerStateChange({ getState, setState }: StateContext<MapConfigModel>) {
        const state = DashGisUtil.clone(getState());
        const scale = this.store.selectSnapshot(ActiveMapState.scale);
        state.visibleRoot = this.removeOutOfRange(DashGisUtil.clone(state.root), scale);
        setState(state);
    }

    @Action(ResetMapConfigState)
    resetState({ getState, setState, dispatch }: StateContext<MapConfigModel>) {
        const state = getState();
        setState(MAPCONFIG_DEFAULT_STATE);
        dispatch(new LoadMapConfig(state.name));
    }

    @Action(PersistUserMapConfigState)
    persistUserState({ getState, dispatch }: StateContext<MapConfigModel>) {
        const state = getState();
        const payload = {
            key: 'layerState',
            value: this.getCurrentUserMapState(state.name, state.root),
        };
        return this.rest.apiPost('auth/user/preference', payload).pipe(tap(() => dispatch(new ReloadLayers())));
    }

    @Action(ResetUserMapConfigState)
    resetUserState({ getState, dispatch }: StateContext<MapConfigModel>) {
        const isUserCustom = getState().userCustom;
        return of(isUserCustom).pipe(
            concatMap((shouldDelete: boolean) => {
                if (shouldDelete) {
                    return this.rest.apiDelete('auth/user/preference/layerState');
                } else {
                    return of(true);
                }
            }),

            tap(() => dispatch(new ReloadLayers()))
        );
    }

    @Action(SetGroupVisibility)
    setGroupVisibility(ctx: StateContext<MapConfigModel>, action: SetGroupVisibility) {
        const newState = DashGisUtil.clone(ctx.getState());
        MapConfigService.traverseGroups(newState.root, action.groupName, (groupName, group) => {
            group.status = action.visible;
            return group;
        });
        ctx.setState(newState);
        ctx.dispatch(new LayerStateChange());
    }

    @Action(SetLayerVisibility)
    setLayerVisibility(ctx: StateContext<MapConfigModel>, action: SetLayerVisibility) {
        const newState = DashGisUtil.clone(ctx.getState());
        MapConfigService.traverseLayers(
            [newState.root],
            action.layerName,
            (layerName, layer, treeContext) => {
                if (action.force) {
                    treeContext.forEach((context) => {
                        context.status = action.visible;
                    });
                }
                layer.status = action.visible;
                return layer;
            },
            () => true
        );
        ctx.setState(newState);
        ctx.dispatch(new LayerStateChange());
    }

    @Action(SetNodeExpandedState)
    setExpanded(ctx: StateContext<MapConfigModel>, action: SetNodeExpandedState) {
        const newState = DashGisUtil.clone(ctx.getState());
        if (action.isGroup) {
            MapConfigService.traverseGroups(newState.root, action.name, (groupName, group) => {
                group.expand = action.expanded;
                return group;
            });
        } else {
            MapConfigService.traverseLayers(
                [newState.root],
                action.name,
                (layerName, layer) => {
                    layer.expand = action.expanded;
                    return layer;
                },
                () => true
            );
        }
        ctx.setState(newState);
        ctx.dispatch(new LayerStateChange());
    }

    private removeOutOfRange(group, scale) {
        for (let i = group.layers.length - 1; i >= 0; i--) {
            const l = group.layers[i];
            if (
                Number.isFinite(l.minScale) &&
                Number.isFinite(l.maxScale) &&
                (scale < l.minScale || scale >= l.maxScale)
            ) {
                group.layers.splice(i, 1);
                continue;
            }
            for (let j = l.classes.length - 1; j >= 0; j--) {
                if (
                    Number.isFinite(l.classes[j].minScale) &&
                    Number.isFinite(l.classes[j].maxScale) &&
                    (scale < l.classes[j].minScale || scale >= l.classes[j].maxScale)
                ) {
                    l.classes.splice(j, 1);
                }
            }
            if (l.classes.length === 0) {
                group.layers.splice(i, 1);
            }
        }
        for (let i = group.groups.length - 1; i >= 0; i--) {
            let g = group.groups[i];
            g = this.removeOutOfRange(g, scale);
            if (g.layers.length === 0 && g.groups.length === 0) {
                group.groups.splice(i, 1);
            }
        }
        return group;
    }

    private getCurrentUserMapState(name: string, root: MsGroupNode): UserMapConfigModel {
        const userRoot: UserMsGroupNode = this.buildUserMapConfig(root);
        return { root: userRoot };
    }

    private buildUserMapConfig(context: MsGroupNode): UserMsGroupNode {
        const returnNode: UserMsGroupNode = {
            name: context.name,
            expand: context.expand,
            status: context.status,
            layers: [],
            groups: [],
        };
        for (const layer of context.layers) {
            returnNode.layers.push({
                name: layer.name,
                expand: layer.expand,
                status: layer.status,
            });
        }
        for (const group of context.groups) {
            returnNode.groups.push(this.buildUserMapConfig(group));
        }

        return returnNode;
    }

    @Selector()
    static config(state: MapConfigModel) {
        return state;
    }

    @Selector()
    static loaded(state: MapConfigModel) {
        return state.loaded;
    }

    @Selector()
    static mapName(state: MapConfigModel) {
        return state.name;
    }

    @Selector()
    static title(state: MapConfigModel) {
        return state.title;
    }

    @Selector()
    static crs(state: MapConfigModel) {
        return state.crs;
    }

    @Selector()
    static projection(state: MapConfigModel) {
        return state.projection;
    }

    @Selector()
    static extent(state: MapConfigModel) {
        return state.extent;
    }

    @Selector()
    static initialExtent(state: MapConfigModel) {
        return state.initialExtent;
    }

    @Selector()
    static refreshInterval(state: MapConfigModel) {
        return state.refreshInterval;
    }

    @Selector()
    static center(state: MapConfigModel) {
        return state.center;
    }

    @Selector()
    static units(state: MapConfigModel) {
        return state.units;
    }

    @Selector()
    static root(state: MapConfigModel) {
        return state.root;
    }

    @Selector()
    static visibleRoot(state: MapConfigModel) {
        return state.visibleRoot;
    }

    @Selector()
    static sortedLayers(state: MapConfigModel) {
        return state.sortedLayers;
    }

    @Selector()
    static styles(state: MapConfigModel) {
        return state.styles;
    }

    @Selector()
    static toolbarWidgets(state: MapConfigModel) {
        return state.toolbarWidgets;
    }

    @Selector()
    static bottomSheetWidgets(state: MapConfigModel) {
        return state.bottomSheetWidgets;
    }

    @Selector()
    static contextMenuItems(state: MapConfigModel) {
        return state.contextMenuItems;
    }

    @Selector()
    static userCustom(state: MapConfigModel) {
        return state.userCustom;
    }
}
